import React from 'react';
import axios from 'axios';

import EditableChip, { type EditableChipOption } from '~/components/core/Molecules/EditableChip';
import CheckedCircle from '~/components/icons/CheckedCircle';
import PendingCircle from '~/components/icons/PendingCircle';
import { reportAxiosError } from '~/Utils';

interface CommunicationHandledStatusChipProps {
  communicationId: number;
  isHandled: boolean;
  disabled?: boolean;
  setIsUpdating?: (isUpdating: boolean) => void;
  onUpdate?: () => Promise<void>;
  displayText?: boolean;
}

const CommunicationHandledStatusChip: React.FC<CommunicationHandledStatusChipProps> = ({
  communicationId,
  isHandled,
  disabled = false,
  setIsUpdating: setIsUpdatingExternal,
  onUpdate,
  displayText = true,
}) => {
  const [isUpdating, setIsUpdatingInternal] = React.useState(false);

  const setIsUpdating = (updating: boolean) => {
    setIsUpdatingInternal(updating);
    if (setIsUpdatingExternal) {
      setIsUpdatingExternal(updating);
    }
  };

  const handleStatusChange = async (optionId: string) => {
    const markAsHandled = optionId === 'handled';
    setIsUpdating(true);
    try {
      const endpoint = markAsHandled
        ? `/api/v1/communications/${communicationId}/mark_as_handled`
        : `/api/v1/communications/${communicationId}/mark_as_unhandled`;
      await axios.post(endpoint);
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const options: EditableChipOption[] = [
    {
      id: 'handled',
      description: (
        <div className="flex items-center gap-4">
          <CheckedCircle size={16} />
          <span>Handled</span>
        </div>
      ),
      displayDescription: displayText ? null : (
        <div className="flex items-center gap-4">
          <CheckedCircle size={16} />
        </div>
      ),
      color: 'teal-600',
    },
    {
      id: 'pending',
      description: (
        <div className="flex items-center gap-4">
          <PendingCircle size={16} />
          <span>Pending</span>
        </div>
      ),
      displayDescription: displayText ? null : (
        <div className="flex items-center gap-4">
          <PendingCircle size={16} />
        </div>
      ),
      color: 'orange-700',
    },
  ];

  const currentValue = isHandled ? 'handled' : 'pending';

  return (
    <div className="mr-8" onClick={(e) => e.stopPropagation()}>
      <EditableChip
        value={currentValue}
        onChange={handleStatusChange}
        options={options}
        disabled={disabled || isUpdating}
        className={displayText ? undefined : 'w-fit max-w-none'}
      />
    </div>
  );
};

export default CommunicationHandledStatusChip;
